/**
 * ChainVault WP - IDO Project Detail Page
 * Full project information before participating
 *
 * @package ChainVault
 * @since   1.0.0
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useWallet } from '../context/WalletContext';
import { ethers } from 'ethers';
import toast from 'react-hot-toast';
import './LaunchpadPage.css';

/* ── Countdown Timer ── */
function Countdown( { endDate } ) {
    const [ timeLeft, setTimeLeft ] = useState( {} );

    useEffect( () => {
        const calc = () => {
            const diff = new Date( endDate ) - new Date();
            if ( diff <= 0 ) { setTimeLeft( null ); return; }
            setTimeLeft({
                days:    Math.floor( diff / (1000*60*60*24) ),
                hours:   Math.floor( (diff % (1000*60*60*24)) / (1000*60*60) ),
                minutes: Math.floor( (diff % (1000*60*60)) / (1000*60) ),
                seconds: Math.floor( (diff % (1000*60)) / 1000 ),
            });
        };
        calc();
        const t = setInterval( calc, 1000 );
        return () => clearInterval(t);
    }, [ endDate ] );

    if ( !timeLeft ) return React.createElement( 'div', { className: 'lp-countdown-ended' }, 'Sale Ended' );

    return React.createElement( 'div', { className: 'lp-countdown' },
        [ ['days','Days'], ['hours','Hrs'], ['minutes','Min'], ['seconds','Sec'] ].map( ([k,l]) =>
            React.createElement( 'div', { key: k, className: 'lp-countdown-item' },
                React.createElement( 'div', { className: 'lp-countdown-num' }, String(timeLeft[k]).padStart(2,'0') ),
                React.createElement( 'div', { className: 'lp-countdown-label' }, l )
            )
        )
    );
}

/* ── Participate Modal ── */
function ParticipateModal( { project, onClose } ) {
    const { address, walletType, builtInWallet } = useWallet();
    const [ amount, setAmount ] = useState('');
    const [ isSending, setIsSending ] = useState(false);
    const isSolana = (project.network||'').toLowerCase() === 'solana';
    const symbol   = isSolana ? 'SOL' : 'ETH';

    const handleParticipate = async () => {
        if ( !amount || isNaN(amount) || parseFloat(amount) <= 0 ) { toast.error('Enter a valid amount.'); return; }
        setIsSending(true);
        try {
            const settings = window.chainvaultSettings || {};
            const fees     = settings.launchpadFees || {};
            const feePct   = parseFloat( fees.idoParticipationFee || 2 ) / 100;
            const total    = parseFloat( amount );
            const toProject= total - (total * feePct);

            if ( isSolana ) {
                const web3 = await import('@solana/web3.js');
                const connection = new web3.Connection('https://api.mainnet-beta.solana.com','confirmed');
                let payer;
                if ( builtInWallet && builtInWallet.solana && builtInWallet.solana.secretKey ) {
                    payer = web3.Keypair.fromSecretKey( new Uint8Array(builtInWallet.solana.secretKey) );
                } else { throw new Error('Built-in Solana wallet required.'); }
                const projectKey = new web3.PublicKey( project.wallet || payer.publicKey.toBase58() );
                const lamports   = Math.floor( toProject * web3.LAMPORTS_PER_SOL );
                const tx = new web3.Transaction().add( web3.SystemProgram.transfer({ fromPubkey: payer.publicKey, toPubkey: projectKey, lamports }) );
                const sig = await web3.sendAndConfirmTransaction(connection, tx, [payer]);
                toast.success('Participated! TX: ' + sig.slice(0,12) + '...');
                onClose();
            } else {
                let signer;
                if ( walletType === 'metamask' && window.ethereum ) {
                    const provider = new ethers.BrowserProvider(window.ethereum);
                    signer = await provider.getSigner();
                } else if ( builtInWallet && builtInWallet.ethereum ) {
                    const provider = new ethers.JsonRpcProvider('https://ethereum-rpc.publicnode.com');
                    signer = new ethers.Wallet(builtInWallet.ethereum.privateKey, provider);
                } else { throw new Error('No wallet connected.'); }
                const projectAddr = project.wallet || await signer.getAddress();
                const tx = await signer.sendTransaction({ to: projectAddr, value: ethers.parseEther(toProject.toString()) });
                toast.success('Participated! TX: ' + tx.hash.slice(0,12) + '...');
                onClose();
            }
        } catch(err) {
            toast.error((err.message||'Failed').slice(0,100));
        } finally { setIsSending(false); }
    };

    return React.createElement( 'div', {
        style: {position:'fixed',inset:0,background:'rgba(0,0,0,0.85)',zIndex:300,display:'flex',alignItems:'center',justifyContent:'center',backdropFilter:'blur(6px)'},
        onClick: onClose
    },
        React.createElement( 'div', {
            style: {background:'#111',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'20px',padding:'28px',width:'100%',maxWidth:'420px',margin:'16px'},
            onClick: e => e.stopPropagation()
        },
            React.createElement('div',{style:{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'20px'}},
                React.createElement('div',{style:{display:'flex',alignItems:'center',gap:'12px'}},
                    React.createElement('div',{style:{width:'40px',height:'40px',borderRadius:'50%',background:'#FC72FF',display:'flex',alignItems:'center',justifyContent:'center',fontWeight:'700',color:'#fff',overflow:'hidden'}},
                        project.logo ? React.createElement('img',{src:project.logo,alt:project.name,style:{width:'40px',height:'40px',objectFit:'cover'},onError:e=>e.target.style.display='none'}) : (project.symbol||'?').charAt(0)
                    ),
                    React.createElement('div',null,
                        React.createElement('div',{style:{fontWeight:'800',color:'#fff',fontSize:'16px'}},project.name),
                        React.createElement('div',{style:{fontSize:'12px',color:'#888'}},project.symbol)
                    )
                ),
                React.createElement('button',{onClick:onClose,style:{background:'none',border:'none',color:'#888',fontSize:'20px',cursor:'pointer'}},'✕')
            ),
            React.createElement('div',{style:{marginBottom:'16px'}},
                React.createElement('label',{style:{display:'block',fontSize:'12px',fontWeight:'600',color:'#aaa',marginBottom:'8px',textTransform:'uppercase',letterSpacing:'0.5px'}},'Amount (' + symbol + ')'),
                React.createElement('input',{type:'number',value:amount,onChange:e=>setAmount(e.target.value),placeholder:'e.g. 0.5 '+symbol,style:{width:'100%',padding:'12px 14px',background:'rgba(255,255,255,0.06)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'10px',color:'#fff',fontSize:'16px',outline:'none',boxSizing:'border-box',fontFamily:'inherit'}})
            ),
            project.min_buy && React.createElement('div',{style:{fontSize:'12px',color:'#888',marginBottom:'4px'}},'Min: '+project.min_buy+' | Max: '+(project.max_buy||'—')),
            project.price && React.createElement('div',{style:{fontSize:'12px',color:'#888',marginBottom:'16px'}},'Token Price: '+project.price),
            React.createElement('button',{onClick:handleParticipate,disabled:isSending,style:{display:'block',width:'100%',padding:'14px',background:'linear-gradient(135deg,#7C63FF,#FC72FF)',border:'none',borderRadius:'12px',color:'#fff',fontWeight:'700',fontSize:'15px',cursor:'pointer',fontFamily:'inherit',opacity:isSending?0.6:1}},
                isSending ? 'Processing...' : '🚀 Participate Now'
            ),
            React.createElement('div',{style:{fontSize:'11px',color:'#555',textAlign:'center',marginTop:'10px'}},'2% platform fee applies')
        )
    );
}

/* ── Main IDO Detail Page ── */
export default function IDODetailPage() {
    const navigate   = useNavigate();
    const { slug }   = useParams();
    const { address, setShowOnboarding } = useWallet();
    const [ showModal, setShowModal ] = useState(false);

    const settings = window.chainvaultSettings || {};
    const projects = Array.isArray(settings.idoProjects) ? settings.idoProjects : [];
    const project  = projects.find( p => (p.name||'').toLowerCase().replace(/\s+/g,'-') === slug );

    if ( !project ) {
        return React.createElement('div',{className:'lp-page'},
            React.createElement('div',{className:'lp-back',onClick:()=>navigate('/launchpad')},'← Back to Launchpad'),
            React.createElement('div',{style:{textAlign:'center',padding:'60px 20px'}},
                React.createElement('div',{style:{fontSize:'48px',marginBottom:'16px'}},'❌'),
                React.createElement('h3',{style:{color:'#fff',marginBottom:'8px'}},'Project Not Found'),
                React.createElement('p',{style:{color:'#aaa'}},'This IDO project does not exist or has been removed.')
            )
        );
    }

    const networkColors = {solana:'#9945FF',ethereum:'#627EEA',bsc:'#F0B90B',polygon:'#8247E5',base:'#0052FF'};
    const netColor  = networkColors[(project.network||'ethereum').toLowerCase()] || '#7C63FF';
    const progress  = Math.min(100, parseInt(project.progress)||0);
    const getStatusClass = s => s==='live'?'live':s==='ended'?'ended':'upcoming';
    const getStatusLabel = s => s==='live'?'● Live':s==='ended'?'Ended':'⏳ Upcoming';

    const socials = [
        project.website    && { icon:'🌐', label:'Website',    url:project.website    },
        project.twitter    && { icon:'𝕏',   label:'Twitter',    url:project.twitter    },
        project.telegram   && { icon:'✈️',  label:'Telegram',   url:project.telegram   },
        project.whitepaper && { icon:'📄',  label:'Whitepaper', url:project.whitepaper },
    ].filter(Boolean);

    return React.createElement('div',{className:'lp-page'},
        React.createElement('div',{className:'lp-back',onClick:()=>navigate('/launchpad')},'← Back to Launchpad'),

        /* Hero Banner */
        React.createElement('div',{className:'lp-ido-detail-hero',style:{background:'linear-gradient(135deg,'+netColor+'22,rgba(0,0,0,0))'}},
            React.createElement('div',{className:'lp-ido-detail-hero-inner'},
                React.createElement('div',{className:'lp-ido-detail-logo'},
                    project.logo
                        ? React.createElement('img',{src:project.logo,alt:project.name,onError:e=>e.target.style.display='none'})
                        : React.createElement('span',null,(project.symbol||'?').charAt(0))
                ),
                React.createElement('div',{className:'lp-ido-detail-hero-info'},
                    React.createElement('div',{style:{display:'flex',alignItems:'center',gap:'12px',flexWrap:'wrap',marginBottom:'8px'}},
                        React.createElement('h1',{className:'lp-ido-detail-name'},project.name),
                        React.createElement('span',{className:'lp-ido-detail-symbol'},project.symbol),
                        React.createElement('span',{className:'lp-ido-card-status '+getStatusClass(project.status)},getStatusLabel(project.status))
                    ),
                    React.createElement('div',{className:'lp-ido-card-network',style:{borderColor:netColor+'44',color:netColor,display:'inline-flex'}},project.network||'Ethereum'),
                    socials.length > 0 && React.createElement('div',{className:'lp-ido-socials'},
                        socials.map((s,i) =>
                            React.createElement('a',{key:i,href:s.url,target:'_blank',rel:'noopener noreferrer',className:'lp-ido-social-btn'},
                                s.icon,' ',s.label
                            )
                        )
                    )
                )
            )
        ),

        /* Main Grid */
        React.createElement('div',{className:'lp-ido-detail-grid'},

            /* Left — Main Content */
            React.createElement('div',{className:'lp-ido-detail-main'},

                /* About */
                project.description && React.createElement('div',{className:'lp-ido-detail-section'},
                    React.createElement('h2',{className:'lp-ido-detail-section-title'},'📋 About '+project.name),
                    React.createElement('p',{className:'lp-ido-detail-about'},project.description)
                ),

                /* Token Details */
                React.createElement('div',{className:'lp-ido-detail-section'},
                    React.createElement('h2',{className:'lp-ido-detail-section-title'},'💎 Token Details'),
                    React.createElement('div',{className:'lp-ido-detail-stats'},
                        [
                            {label:'Token Price',   value:project.price         ||'TBA'},
                            {label:'Target Raise',  value:project.raise         ||'TBA'},
                            {label:'Total Supply',  value:project.total_supply  ||'TBA'},
                            {label:'Network',       value:project.network       ||'TBA'},
                            {label:'Min Buy',       value:project.min_buy       ||'TBA'},
                            {label:'Max Buy',       value:project.max_buy       ||'TBA'},
                            {label:'Start Date',    value:project.start_date    ? new Date(project.start_date).toLocaleDateString()  : 'TBA'},
                            {label:'End Date',      value:project.end_date      ? new Date(project.end_date).toLocaleDateString()    : 'TBA'},
                        ].map((s,i) =>
                            React.createElement('div',{key:i,className:'lp-ido-detail-stat'},
                                React.createElement('div',{className:'lp-ido-stat-label'},s.label),
                                React.createElement('div',{className:'lp-ido-stat-value'},s.value)
                            )
                        )
                    )
                ),

                /* Contract */
                project.contract && React.createElement('div',{className:'lp-ido-detail-section'},
                    React.createElement('h2',{className:'lp-ido-detail-section-title'},'📝 Contract Address'),
                    React.createElement('div',{className:'lp-result-address'},
                        React.createElement('code',null,project.contract),
                        React.createElement('button',{onClick:()=>{navigator.clipboard.writeText(project.contract);toast.success('Copied!');}}, 'Copy')
                    )
                ),

                /* How to Participate */
                React.createElement('div',{className:'lp-ido-detail-section'},
                    React.createElement('h2',{className:'lp-ido-detail-section-title'},'🚀 How to Participate'),
                    React.createElement('ol',{className:'lp-ido-steps-list'},
                        ['Connect your wallet in ChainVault Settings',
                         'Review the token details carefully',
                         'Enter the amount you want to invest',
                         'Click Participate and confirm in your wallet',
                         'You will receive your tokens after the sale ends'
                        ].map((s,i) => React.createElement('li',{key:i},s))
                    )
                ),

                /* Risk Disclaimer */
                React.createElement('div',{className:'lp-ido-disclaimer'},
                    React.createElement('h4',null,'⚠️ Risk Disclaimer'),
                    React.createElement('p',null,'Participating in IDO presales involves significant financial risk. Token prices can go to zero. Always do your own research (DYOR) before investing. Never invest more than you can afford to lose. ChainVault is not responsible for project performance.')
                )
            ),

            /* Right — Participate Panel */
            React.createElement('div',{className:'lp-ido-detail-side'},

                /* Progress Card */
                React.createElement('div',{className:'lp-ido-detail-card'},
                    React.createElement('div',{className:'lp-ido-detail-card-title'},'Sale Progress'),
                    React.createElement('div',{className:'lp-ido-progress-wrap',style:{marginBottom:'16px'}},
                        React.createElement('div',{className:'lp-ido-progress-top'},
                            React.createElement('span',null,progress+'% Filled'),
                            React.createElement('span',null,project.raise||'')
                        ),
                        React.createElement('div',{className:'lp-ido-progress-bar'},
                            React.createElement('div',{className:'lp-ido-progress-fill',style:{width:progress+'%'}})
                        )
                    ),
                    project.end_date && project.status !== 'ended' && React.createElement('div',null,
                        React.createElement('div',{style:{fontSize:'12px',color:'#888',marginBottom:'10px',textAlign:'center'}},'Sale ends in:'),
                        React.createElement(Countdown,{endDate:project.end_date})
                    ),
                    project.status !== 'ended'
                        ? React.createElement('button',{
                            className:'lp-btn-primary',
                            style:{marginTop:'16px'},
                            onClick:()=>{ if(!address){setShowOnboarding(true);return;} setShowModal(true); }
                          }, project.status==='live' ? '🚀 Participate Now' : '🔔 Notify Me When Live')
                        : React.createElement('button',{className:'lp-btn-primary',style:{marginTop:'16px',opacity:0.5,cursor:'not-allowed'}},'Sale Ended')
                ),

                /* Token Price Card */
                React.createElement('div',{className:'lp-ido-detail-card'},
                    React.createElement('div',{className:'lp-ido-detail-card-title'},'Token Price'),
                    React.createElement('div',{style:{fontSize:'28px',fontWeight:'900',color:'#fff',margin:'8px 0'}},project.price||'TBA'),
                    project.min_buy && React.createElement('div',{style:{fontSize:'12px',color:'#888'}},'Min: '+project.min_buy),
                    project.max_buy && React.createElement('div',{style:{fontSize:'12px',color:'#888'}},'Max: '+project.max_buy)
                ),

                /* Safety Tips */
                React.createElement('div',{className:'lp-info-box'},
                    React.createElement('h4',null,'🛡️ Safety Tips'),
                    React.createElement('ul',{style:{paddingLeft:'16px',margin:0}},
                        ['Always verify the contract address','Never share your seed phrase','Only invest what you can afford to lose','Check social links are official'].map((t,i)=>
                            React.createElement('li',{key:i,style:{fontSize:'12px',color:'#777',marginBottom:'6px',lineHeight:'1.5'}},t)
                        )
                    )
                )
            )
        ),

        showModal && React.createElement(ParticipateModal,{project,onClose:()=>setShowModal(false)})
    );
}
